import { Check, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../lib/google-maps-loader';
import { Input } from '../../ui/input';

export interface AddressResult {
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  googlePlaceId: string | null;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelectAddress: (result: AddressResult) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  apiKey?: string;
  initialLatitude?: number | null;
  initialLongitude?: number | null;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelectAddress,
  placeholder = 'Search address or place...',
  required = false,
  className,
  apiKey,
  initialLatitude,
  initialLongitude,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<{
    addListener: (event: string, handler: () => void) => void;
    getPlace: () => {
      formatted_address?: string;
      name?: string;
      place_id?: string;
      geometry?: {
        location?: {
          lat: () => number;
          lng: () => number;
        };
      };
    };
  } | null>(null);
  const [hasValidCoords, setHasValidCoords] = useState(
    initialLatitude != null && initialLongitude != null
  );

  // Keep the latest callbacks in refs so the effect below can stay mounted-once (see
  // its dependency array) instead of tearing down and recreating the Google Autocomplete
  // instance on every keystroke -- that respawn was swallowing the user's first click on
  // a suggestion (a new instance/listener attaches mid-click), requiring a second select.
  const onChangeRef = useRef(onChange);
  const onSelectAddressRef = useRef(onSelectAddress);
  useEffect(() => {
    onChangeRef.current = onChange;
    onSelectAddressRef.current = onSelectAddress;
  }, [onChange, onSelectAddress]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps(apiKey)
      .then((googleMaps) => {
        if (!isMounted || !inputRef.current || !googleMaps.places) return;

        autocompleteRef.current = new googleMaps.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry', 'place_id', 'name'],
        });

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          if (!place) return;

          const formattedAddress = place.formatted_address || place.name || '';
          const lat = place.geometry?.location ? place.geometry.location.lat() : null;
          const lng = place.geometry?.location ? place.geometry.location.lng() : null;
          const googlePlaceId = place.place_id || null;

          onChangeRef.current(formattedAddress);
          setHasValidCoords(lat != null && lng != null);

          onSelectAddressRef.current({
            formattedAddress,
            latitude: lat,
            longitude: lng,
            googlePlaceId,
          });
        });
      })
      .catch(() => {
        // Fallback gracefully to regular text input
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  return (
    <div className="space-y-1.5 w-full">
      <div className="relative flex items-center">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setHasValidCoords(false);
          }}
          placeholder={placeholder}
          required={required}
          className={`pl-9 pr-8 ${className || ''}`}
        />
        {hasValidCoords && (
          <div
            className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center pointer-events-none"
            title="Verified geocoded address"
          >
            <Check className="h-3 w-3 stroke-[3]" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-[11px] text-muted-foreground px-0.5">
        <span>
          {hasValidCoords ? (
            <span className="text-emerald-600 font-medium flex items-center gap-1">
              <Check className="h-3 w-3" /> Geocoded with GPS coordinates for map & routing
            </span>
          ) : (
            'Select a suggested address from the dropdown to automatically plot coordinates.'
          )}
        </span>
      </div>
    </div>
  );
}
